// admin.ts — DTOs for the internal ops admin platform (Task 2 of the admin-platform cycle,
// spec docs/superpowers/specs/2026-08-06-admin-platform-design.md §3). Backed by
// `createAdminRepos` (`@repo/db`), consumed by `apps/api/src/routes/admin.ts`. Page-NUMBER
// pagination (not the keyset/cursor style the customer-facing list DTOs use) — only ops
// staff hit these, and "page 3 of the Workspaces table" is a meaningful, stable request to
// repeat, unlike an infinite-scroll feed.
import type {
  BotStatus,
  DemoStatus,
  JobKind,
  JobStatus,
  MemberRole,
  PlanCode,
  SourceStatus,
  SourceType,
  SubscriptionStatus,
} from './enums.ts';
import type { AdminFeedbackDto } from './feedback.ts';

/** Shared page envelope — every admin list route returns this shape wrapping its own
 * `items`. `pageSize` is always 50 (spec §2), included on the wire rather than hardcoded
 * client-side so the dashboard never has to duplicate that constant. */
export interface AdminPageMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** `GET /api/admin/workspaces` row. `plan`/`cancelAtPeriodEnd`/`messagesThisMonth`/
 * `pagesTotal` are computed by the EXACT SAME per-workspace logic backing
 * `GET /api/billing/summary` (`createBillingViewRepo`) — see `admin-overview.ts`'s header
 * comment — so this screen and a workspace's own Billing screen can never disagree. */
export interface AdminWorkspaceRow {
  id: string;
  name: string;
  createdAt: string;
  /** The workspace's owner `User.email` (oldest `WorkspaceMember` with `role: 'owner'`).
   * `null` only in the pathological case of a workspace with no owner row. */
  ownerEmail: string | null;
  plan: PlanCode;
  cancelAtPeriodEnd: boolean;
  botsCount: number;
  /** Sum of `UsageEvent.qty` where `kind = 'message'`, current UTC calendar month — same
   * number `BillingSummaryDto.usage.messagesThisMonth` reports for this workspace. */
  messagesThisMonth: number;
  /** Sum of `DataSource.pageCount` across every bot in the workspace — same number
   * `BillingSummaryDto.usage.pagesTotal` reports for this workspace. */
  pagesTotal: number;
  /** Admin v2 / internal workspaces (spec §A) — `Workspace.internal`. Drives the admin
   * Workspaces table's INTERNAL chip and the internal-toggle switch's initial state. */
  internal: boolean;
  /** Count of `WorkspaceMember` rows (workspace-logo-and-admin-members spec §B). Drives the
   * Workspaces table's Members column. */
  membersCount: number;
}

/** `POST /api/admin/workspaces/:id/internal` request/response (spec §A). Body is
 * `{ internal: boolean }` (validated by `@repo/validation`'s `setWorkspaceInternalSchema`);
 * the response echoes back the resolved state so the dashboard can update its row/switch
 * optimistically-confirmed rather than re-fetching the whole page. */
export interface AdminWorkspaceInternalDto {
  id: string;
  internal: boolean;
}

/**
 * `GET /api/admin/workspaces`'s admin signal (source-detail-diagnostics Task 6, design spec
 * "Admin signal"): two numbers, deliberately not a chart/dashboard/alert, whose only job is
 * to tell Johnny when the $5/mo Cloudflare Workers Paid upgrade is worth making. Both exclude
 * internal (dogfood) workspaces — this signal sizes CUSTOMER demand, and our own usage would
 * otherwise put a permanent floor under it regardless of what customers are doing.
 *
 * - `renderedModeSources`: sources with `renderedMode: true` right now, and the distinct
 *   bots they belong to — how much of the fleet depends on Cloudflare Browser Rendering at
 *   all. Deliberately never says "paid": we are on Workers FREE today
 *   (docs/design/001-PRD.md §3) — that's the whole premise this signal exists to inform.
 * - `capacityTruncatedSources7d`: distinct sources with a `truncated_capacity` crawl run
 *   (OUR OWN Cloudflare rendering capacity ran out — never `truncated_page_cap`, a
 *   customer's plan cap) in the last 7 days, measured on the database clock. This
 *   structurally UNDERCOUNTS: a killed process or a reaped stale job leaves no `CrawlRun`
 *   row at all, so THAT case never shows up here — a handler that throws and gets caught
 *   DOES write a `CrawlRun` (outcome `failed`), so it is not what "undercounts" refers to.
 *   It can only ever be a floor.
 */
export interface AdminCrawlSignalDto {
  renderedModeSources: { sourceCount: number; botCount: number };
  capacityTruncatedSources7d: { sourceCount: number };
}

export interface AdminWorkspacesOverviewDto extends AdminPageMeta {
  items: AdminWorkspaceRow[];
  /** Optional (final review, source-detail-diagnostics whole-branch review), matching what's
   * actually true at the wire: `apps/dashboard` (CF Pages) and `apps/api` (Railway) deploy
   * independently, so a dashboard deploy that lands first can hit an API that hasn't shipped
   * this field yet. `AdminWorkspaces.tsx` already guards on `data?.signal &&` for exactly this
   * split-deploy window (review fix round 1, Minor 4) — the guard was correct, this type
   * claiming the field is always present was not. */
  signal?: AdminCrawlSignalDto;
}

/** `GET /api/admin/bots` row. */
export interface AdminBotRow {
  id: string;
  publicId: string;
  name: string;
  status: BotStatus;
  createdAt: string;
  workspaceName: string;
  /** The bot's workspace owner email — same resolution rule as `AdminWorkspaceRow.ownerEmail`. */
  ownerEmail: string | null;
  sourcesCount: number;
  aiSearchInstanceId: string | null;
  /** Latest `DataSource.lastSyncedAt` across the bot's sources; `null` when the bot has no
   * sources or none has synced yet. */
  lastCrawledAt: string | null;
}

export interface AdminBotsOverviewDto extends AdminPageMeta {
  items: AdminBotRow[];
}

export interface AdminJobCount {
  status: JobStatus;
  kind: JobKind;
  count: number;
}

/** One row in `AdminJobsOverviewDto.failedJobs`. `payloadSummary` is ALWAYS a truncated
 * (first 200 chars) string rendering of `Job.payload` — the raw JSON payload is never sent
 * over the wire to the admin UI (spec §2/§3: "never render full payload in UI"). `error` is
 * `Job.lastError` verbatim (untruncated at this layer; the dashboard truncates for display
 * with a `title` attribute per spec §4). */
export interface AdminFailedJobRow {
  id: string;
  kind: JobKind;
  error: string | null;
  attempts: number;
  updatedAt: string;
  payloadSummary: string;
}

/** `GET /api/admin/jobs` response — counts grouped by (status, kind) across the WHOLE table
 * (not paginated — there are only as many rows as `JobStatus × JobKind` combinations that
 * have ever occurred), plus the 50 most recently updated failed jobs. */
export interface AdminJobsOverviewDto {
  counts: AdminJobCount[];
  failedJobs: AdminFailedJobRow[];
}

/** `POST /api/admin/jobs/:id/retry` success response. Failure is a 409 `JOB_NOT_RETRYABLE`
 * (`ApiFailure`), never a body shape of its own — see `routes/admin.ts`. */
export interface AdminRetryJobResponseDto {
  job: {
    id: string;
    kind: JobKind;
    status: JobStatus;
    attempts: number;
    updatedAt: string;
  };
}

// ── Admin v2 (spec docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B) ──

/** The workspace's owner, as surfaced on the workspace detail screen's danger zone (ban/role
 * controls). `null` only in the pathological ownerless-workspace case (same as
 * `AdminWorkspaceRow.ownerEmail`). `role`/`banned` come straight off `User` — better-auth's
 * `admin()` plugin fields (see `packages/db/prisma/schema.prisma`'s `User` model comment). */
export interface AdminWorkspaceOwner {
  userId: string;
  email: string;
  role: string | null;
  banned: boolean;
}

/** One row in `AdminWorkspaceDetailDto.bots` — deliberately a small subset of `AdminBotRow`
 * (no `workspaceName`/`ownerEmail`, both already known from the surrounding detail page). */
export interface AdminWorkspaceDetailBotRow {
  id: string;
  publicId: string;
  name: string;
  status: BotStatus;
  createdAt: string;
}

/** One row in `AdminWorkspaceDetailDto.members` (workspace-logo-and-admin-members spec §B) —
 * read-only, no admin-side role changes (owners manage their own members via `/api/members`).
 * `name` mirrors `User.name`, which is nullable. */
export interface AdminWorkspaceMemberRowDto {
  userId: string;
  email: string;
  name: string | null;
  role: MemberRole;
  joinedAt: string;
}

/** Latest subscription snapshot for the workspace detail's subscription block —
 * `polarSubscriptionId` is rendered as plain text plus a "manage in Polar" external link
 * (spec §B1), never acted on directly from this screen (no plan-change UI, per spec's NO
 * PLAN-CHANGE UI line — Polar's own dashboard is the tool). `null` when the workspace has
 * never had an entitled subscription (still on the free plan). */
export interface AdminSubscriptionBlock {
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  polarSubscriptionId: string;
}

/** `GET /api/admin/workspaces/:id` response body. `usage`/`plan`/subscription fields are
 * computed by the SAME `createBillingViewRepo` calls backing `AdminWorkspaceRow` and
 * `GET /api/billing/summary` (see `admin-overview.ts`) — this detail screen can never
 * disagree with either. */
export interface AdminWorkspaceDetailDto {
  id: string;
  name: string;
  createdAt: string;
  internal: boolean;
  owner: AdminWorkspaceOwner | null;
  plan: PlanCode;
  usage: {
    messagesThisMonth: number;
    pagesTotal: number;
    botsCount: number;
  };
  subscription: AdminSubscriptionBlock | null;
  bots: AdminWorkspaceDetailBotRow[];
  /** Every `WorkspaceMember` in the workspace, owner first then `joinedAt` ascending (spec
   * §B). Read-only on this screen. */
  members: AdminWorkspaceMemberRowDto[];
}

/** One row in `AdminBotDetailDto.sources` — the bot detail screen's sources table (spec
 * §B2: "url, status, pages, lastCrawledAt, error"). */
export interface AdminBotDetailSourceRow {
  id: string;
  uri: string;
  type: SourceType;
  status: SourceStatus;
  pageCount: number;
  lastCrawledAt: string | null;
  error: string | null;
}

/** One row in `AdminBotDetailDto.recentJobs` — best-effort linkage via `Job.payload`'s
 * `botId`/`sourceId` (spec §B2), never the full payload (same redaction discipline as
 * `AdminFailedJobRow.payloadSummary` / `AdminJobRow.payloadSummary`). */
export interface AdminJobSummaryRow {
  id: string;
  kind: JobKind;
  status: JobStatus;
  attempts: number;
  updatedAt: string;
  payloadSummary: string;
}

/** `GET /api/admin/bots/:id` response body. */
export interface AdminBotDetailDto {
  id: string;
  publicId: string;
  name: string;
  status: BotStatus;
  aiSearchInstanceId: string | null;
  createdAt: string;
  workspaceId: string;
  workspaceName: string;
  sources: AdminBotDetailSourceRow[];
  recentJobs: AdminJobSummaryRow[];
}

/** Tabs on the Jobs v2 screen (spec §B3: "All|Running|Failed"). Not the full `JobStatus`
 * union — `pending`/`done` jobs are reachable through `'all'` but get no dedicated tab. */
export const ADMIN_JOB_STATUS_FILTERS = ['all', 'running', 'failed'] as const;
export type AdminJobStatusFilter = (typeof ADMIN_JOB_STATUS_FILTERS)[number];

/** One row in `GET /api/admin/jobs?status=&page=`'s paginated `items` — a generalized
 * sibling of `AdminFailedJobRow` that isn't restricted to failed jobs. */
export interface AdminJobRow {
  id: string;
  kind: JobKind;
  status: JobStatus;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  payloadSummary: string;
}

/** One tab's badge count on the Jobs v2 screen — deliberately simpler than `AdminJobCount`
 * (no `kind` breakdown; that one backs the legacy `/api/admin/jobs` overview's per-kind
 * table, this one just backs "All (123) / Running (4) / Failed (2)" tab labels). */
export interface AdminJobStatusCount {
  status: JobStatus;
  count: number;
}

/** `GET /api/admin/jobs?status=&page=` response body (spec §B3). `counts` is ALWAYS grouped
 * across the WHOLE table regardless of the requested `status` filter (one entry per
 * `JobStatus`, zero-filled) — that's what lets the UI render every tab's badge without three
 * separate requests. Calling `GET /api/admin/jobs` with NEITHER `status` NOR `page` set keeps
 * returning the legacy `AdminJobsOverviewDto` shape instead (back-compat, see
 * `routes/admin.ts`). */
export interface AdminJobsFilteredDto extends AdminPageMeta {
  items: AdminJobRow[];
  counts: AdminJobStatusCount[];
}

/** `GET /api/admin/jobs/:id` response body — the one place the admin UI is allowed to render
 * a job's FULL payload (spec §B3: "admin-only is acceptable — payloads contain no secrets").
 * `payload` has still been run through the same defensive redaction as every other payload
 * surface (see `redactPayload` in `admin-overview.ts`) in case a future job kind ever adds a
 * field named like a credential. */
export interface AdminJobDetailDto {
  id: string;
  kind: JobKind;
  status: JobStatus;
  attempts: number;
  runAfter: string;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  payload: unknown;
}

/** `POST /api/admin/jobs/:id/cancel` success response. Failure is a 409
 * `JOB_NOT_CANCELLABLE` (`ApiFailure`) — collapses "no such job" and "not pending" into one
 * wire status, same convention as `AdminRetryJobResponseDto`/`JOB_NOT_RETRYABLE`. */
export interface AdminCancelJobResponseDto {
  job: {
    id: string;
    kind: JobKind;
    status: JobStatus;
    attempts: number;
    updatedAt: string;
  };
}

/** `POST /api/admin/users/:id/{ban,unban}` and `POST /api/admin/users/:id/role` all resolve
 * to this same shape — the updated user's admin-relevant fields. */
export interface AdminUserActionDto {
  user: AdminWorkspaceOwner;
}

/** `GET /api/admin/feedback?handled=all|false&page=` response body (feedback-form design,
 * docs/superpowers/specs/2026-08-18-feedback-form-design.md). Same `AdminPageMeta` envelope
 * every other admin list route returns (`page`/`pageSize`/`totalCount`/`totalPages`), not the
 * `{items, page, pageSize, total}` shape the task brief first sketched — the existing
 * convention wins (see `AdminJobsFilteredDto` above), so `totalCount`/`totalPages` are the
 * wire field names here too, not `total`. */
export interface AdminFeedbackListDto extends AdminPageMeta {
  items: AdminFeedbackDto[];
}

/** `GET /api/admin/feedback/unhandled-count` response body — the admin nav tab's count-chip
 * badge (spec: "shows a small count chip when unhandled-count > 0"). */
export interface AdminFeedbackUnhandledCountDto {
  count: number;
}

// ── Playground analytics (THI-103, Task 1 of the playground-analytics cycle) — Task 2 (the
// dashboard screen) consumes these verbatim. ──

/** One day's count in a `demosPerDay`/`messagesPerDay` series. */
export interface AdminPlaygroundDayCountDto {
  /** UTC `YYYY-MM-DD`. */
  date: string;
  count: number;
}

/** One contact captured inside a playground demo (2026-08-22 playground-product-demo spec
 * §A). Platform-Admin-only and read-only — these are OUR leads, not a customer's. */
export interface AdminPlaygroundLeadDto {
  email: string | null;
  name: string | null;
  /** What the visitor was asking about when they left contact details. */
  intent: string | null;
  createdAt: string;
}

/** `recentDomains` row — the latest demo for a given hostname, deduped from the latest-100
 * `DemoBot` rows. */
export interface AdminPlaygroundDomainDto {
  hostname: string;
  /** Most recent demo's full URL for this hostname. */
  url: string;
  status: DemoStatus;
  pageCount: number;
  messageCount: number;
  /** ISO timestamp of the most recent demo for this hostname. */
  createdAt: string;
  /** How many demos of this hostname appear in the latest-100 window. */
  attempts: number;
  /** True when ANY demo of this hostname inside the latest-100 window converted
   * (OR-accumulated across the same rows `attempts` counts), not only the most recent one. */
  signedUp: boolean;
  /** Leads captured across every demo of this hostname inside the latest-100 window —
   * summed across the same rows `attempts` counts, like `signedUp` is OR-accumulated. */
  leadCount: number;
  /** The contacts behind `leadCount`, newest first, for the admin's inline expand. Bounded
   * by construction: `DemoLead` is unique on `(demoBotId, sessionKey)` and a demo has
   * exactly one session, so this is at most one entry per attempt. */
  leads: AdminPlaygroundLeadDto[];
}

/** `GET /api/admin/playground` response body — the playground analytics screen's whole
 * payload in one call (deliberately not paginated like the workspace/bot/job lists above:
 * this is a fixed-shape dashboard, not a browsable table). */
export interface AdminPlaygroundOverviewDto {
  totals: {
    demosAllTime: number;
    demos30d: number;
    /** Sum of DemoUsageDay.messages, all time. */
    messagesAllTime: number;
    /** Count over `DemoBot ∪ DemoArchive` of rows with `signedUpAt != null`, all time
     * (2026-08-22 playground-signup-card spec §B). */
    signedUpAllTime: number;
    /** Same count, windowed on `createdAt` — same 30-day window `demos30d` uses. */
    signedUp30d: number;
    /** `DemoLead ∪ DemoArchiveLead`, all time. */
    leadsAllTime: number;
    /** Same union, windowed on the LEAD's own `createdAt` — not the demo's, unlike
     * `demos30d`/`signedUp30d`. A lead is its own event with its own timestamp, and an
     * archived lead carries the original one. */
    leads30d: number;
  };
  /** Last 30 UTC days, oldest first; days with zero demos are ABSENT (UI fills gaps). */
  demosPerDay: AdminPlaygroundDayCountDto[];
  /** Last 30 UTC days from DemoUsageDay, oldest first; zero days absent. */
  messagesPerDay: AdminPlaygroundDayCountDto[];
  /** Demos created in the last 30 days grouped by current status, descending count. */
  statusBreakdown30d: { status: DemoStatus; count: number }[];
  /** Latest-first, deduped by hostname (from the latest 100 demos), max 50. */
  recentDomains: AdminPlaygroundDomainDto[];
}
